import { api, tokenStore, ApiError, USUARIO_KEY } from './client';
import { esFallaDeRed } from '../lib/conexion';

export type ApiRol =
  // ---- Roles vigentes (modelo de 6 roles, jun-2026) ----
  | 'gerente_administrativo' // SUPER ADMIN (Paola) — todo menos debug/docs; crea usuarios
  | 'administrador'          // ADMIN por sede — todo de SU sede, NO crea usuarios
  | 'mesero'                 // operación de salón
  | 'cocina'                 // pedidos + inventario + carta
  | 'cajera'                 // CAJERO (vigente oct-2026): mesero + inventario/gastos/activos; borrar o reducir pide aprobación
  | 'contador'               // contabilidad / reportes
  | 'desarrollador'          // todo, incl. debug; crea usuarios
  // ---- Legacy (existen en datos viejos; ya no se ofrecen al crear usuarios) ----
  | 'gerente_operativo'
  | 'jefe_operaciones'
  | 'jefe_administrativo'
  | 'public_user';

export interface ApiUser {
  id: number;
  username: string;
  nombre: string;
  rol: ApiRol;
  sede_id: number | null;
  sede_codigo: string | null;
  sede_nombre: string | null;
}

interface LoginResponse {
  token: string;
  expira_at: string;
  usuario: ApiUser;
}

export async function login(username: string, password: string): Promise<ApiUser> {
  const r = await api.post<LoginResponse>('/api/auth/login', { username, password });
  tokenStore.set(r.token);
  guardarUsuario(r.usuario);
  return r.usuario;
}

// ---------------------------------------------------------------------------
// Sesión sin internet (modo contingencia, paso 3b · oct-2026)
//
// El PC de caja tiene que poder reiniciarse o recargar sin internet y seguir
// en la caja de contingencia. Para eso se recuerda el último usuario que el
// servidor confirmó. Solo se usa si /api/auth/me no responde POR RED; un 401
// sigue siendo "sesión vencida, volvé a entrar".
// ---------------------------------------------------------------------------
function guardarUsuario(u: ApiUser) {
  try { localStorage.setItem(USUARIO_KEY, JSON.stringify(u)); } catch { /* sin storage: solo en línea */ }
}

/** El último usuario confirmado, si todavía hay token. */
export function usuarioGuardado(): ApiUser | null {
  if (!tokenStore.get()) return null;
  try {
    const u = JSON.parse(localStorage.getItem(USUARIO_KEY) || 'null');
    return u && typeof u.id === 'number' && typeof u.rol === 'string' ? (u as ApiUser) : null;
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  try { await api.post('/api/auth/logout', {}); } catch { /* ignora errores de red al salir */ }
  tokenStore.set(null);
}

/**
 * El usuario de la sesión. Sin token o con la sesión rechazada: null.
 * Sin red (o sin respuesta de la API): LANZA y conserva el token. Antes lo
 * borraba, y un PC que arrancaba sin internet quedaba fuera aunque la sesión
 * siguiera vigente.
 */
export async function fetchMe(): Promise<ApiUser | null> {
  if (!tokenStore.get()) return null;
  try {
    const u = await api.get<ApiUser>('/api/auth/me');
    guardarUsuario(u);
    return u;
  } catch (e) {
    if (e instanceof ApiError && esFallaDeRed(e.status)) throw e;
    tokenStore.set(null);
    return null;
  }
}
